import React, { useMemo, type Dispatch, type SetStateAction } from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import DynamicTable from '@atlaskit/dynamic-table';
import type { RankEndLocation } from '@atlaskit/dynamic-table/types';
import { Flex, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { PAGE_SIZE } from '../../common/constants';
import type {
	PrioritiesTablePage,
	Priority,
	PriorityCurrentModalState,
	PriorityCurrentSidebarState,
	PriorityFormAsyncErrors,
	ReorderHandler,
} from '../../common/types';
import { Actions } from './actions';
import { Schemes } from './schemes';
import { generateTableHeader, generateFillerRows } from './utils';

type PrioritiesTableProps = {
	page: PrioritiesTablePage;
	currentPageNumber: number;
	focusedPriorityId?: string;
	isLoading: boolean;
	onFetch: (pageNumber: number) => void;
	onReorder: ReorderHandler;
	setCurrentModal: Dispatch<SetStateAction<PriorityCurrentModalState>>;
	setCurrentSidebar: Dispatch<SetStateAction<PriorityCurrentSidebarState>>;
	setAsyncFormErrors: Dispatch<SetStateAction<PriorityFormAsyncErrors>>;
	onClosePriorityModal?: () => void;
	onDeleteConfirm?: (priority: Priority) => void;
	onSetPriorityPosition?: (priorityId: string, order: number) => Promise<void>;
};

type IOnRankEnd = (arg0: { sourceIndex: number; destination?: RankEndLocation }) => void;

export const PrioritiesTable = ({
	page,
	currentPageNumber,
	isLoading,
	focusedPriorityId,
	setCurrentModal,
	setCurrentSidebar,
	setAsyncFormErrors,
	onFetch,
	onReorder,
	onClosePriorityModal,
	onDeleteConfirm,
	onSetPriorityPosition,
}: PrioritiesTableProps) => {
	const intl = useIntl();
	const { items, pageNumber, pageSize, totalItems } = page;
	const head = generateTableHeader(intl);

	const rows = useMemo(() => {
		const totalPages = Math.ceil(totalItems / PAGE_SIZE);
		const isFirstPage = currentPageNumber === 1;
		const isLastPage = currentPageNumber === totalPages;
		const firstPositionOnPage = (currentPageNumber - 1) * PAGE_SIZE + 1;

		const handleOnEdit = (item: Priority) => {
			setAsyncFormErrors(null);
			setCurrentSidebar({
				sidebarName: 'EDIT',
				priority: item,
			});
		};

		const visibleRows = items.map((item, index) => ({
			key: `priorities-table-row-${item.id}`,
			cells: [
				{
					id: `priorities-table-row-${item.id}-position-number`,
					content: <p>{firstPositionOnPage + index}</p>,
				},
				{
					id: `priorities-table-row-${item.id}-icon-and-name`,
					content: (
						<Inline xcss={iconAndNameTableRowStyles}>
							<Avatar size="small" src={item.iconUrl} name={item.name} />
							<span>{item.name}</span>
						</Inline>
					),
				},
				{
					id: `priorities-table-row-${item.id}-description`,
					content: <p>{item.description}</p>,
				},
				{
					id: `priorities-table-row-${item.id}-color`,
					content: <StatusColor color={item.statusColor} />,
				},
				{
					id: `priorities-table-row-${item.id}-schemes`,
					content: <Schemes priority={item} />,
				},
				{
					id: `priorities-table-row-${item.id}-actions`,
					content: (
						<Flex justifyContent="end" xcss={actionStyles}>
							<Actions
								priority={item}
								canMoveUp={!(isFirstPage && index === 0)}
								canMoveDown={!(isLastPage && index === items.length - 1)}
								shouldFocusMoveButton={item.id === focusedPriorityId}
								onEdit={() => handleOnEdit(item)}
								onDelete={() => {
									setCurrentSidebar(null);
									setCurrentModal({
										modalName: item.schemes.total > 0 ? 'DELETE_IN_USE' : 'DELETE',
										priority: item,
									});
								}}
								onReorder={onReorder}
								onSelectPosition={() =>
									setCurrentModal({
										modalName: 'MOVE',
										priority: item,
									})
								}
								{...(fg('jsw_priority_modals_admin_page')
									? {
											onClosePriorityModal,
											onDeleteConfirm,
											onSetPriorityPosition,
											totalItems,
										}
									: {})}
							/>
						</Flex>
					),
				},
			],
		}));

		// Force the correct number of pages to be rendered by padding out the number of rows
		const rowsBefore = generateFillerRows((currentPageNumber - 1) * PAGE_SIZE);
		const rowsAfter = generateFillerRows((totalPages - currentPageNumber) * PAGE_SIZE);

		return [...rowsBefore, ...visibleRows, ...rowsAfter];
	}, [
		totalItems,
		currentPageNumber,
		items,
		setAsyncFormErrors,
		setCurrentSidebar,
		focusedPriorityId,
		onReorder,
		onClosePriorityModal,
		onDeleteConfirm,
		onSetPriorityPosition,
		setCurrentModal,
	]);

	const onRankEnd: IOnRankEnd = ({ sourceIndex, destination }) => {
		if (!destination || sourceIndex === destination.index) {
			return;
		}

		const priorityId = items[sourceIndex]?.id;
		onReorder({
			priorityId,
			action: 'DRAG',
			dragSourceIndex: sourceIndex,
			dragDestinationIndex: destination.index,
		});
	};

	return (
		<DynamicTable
			head={head}
			isFixedSize
			isLoading={isLoading}
			onSetPage={onFetch}
			rows={rows}
			rowsPerPage={pageSize}
			page={pageNumber}
			isRankable
			onRankEnd={onRankEnd}
		/>
	);
};

const iconAndNameTableRowStyles = xcss({
	alignItems: 'center',
	gap: 'space.075',
});

const actionStyles = xcss({
	marginRight: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatusColor = styled.div<{ color: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: `${({ color }: { color: string }) => color}`,
	borderRadius: token('space.075', '6px'),
	height: token('space.300', '24px'),
	width: token('space.300', '24px'),
});
